import { useCallback, useEffect, useRef, useState } from "react";

import { useNubankFeatureFlagsContext } from "../utils/featureFlagsContext";
import { goToNativeMyTripsFromCheckout } from "../utils/nubankHandoff";

export const NATIVE_HANDOFF_TIMEOUT_MS = 10_000;

export const useCheckoutMyTripsNavigation = (
  navigateToLegacyMyTrips: () => void
) => {
  const { enableExternalTripsList } = useNubankFeatureFlagsContext();
  const nativeHandoffPendingRef = useRef(false);
  const nativeHandoffTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [nativeHandoffPending, setNativeHandoffPending] = useState(false);

  const resetNativeHandoff = useCallback(() => {
    if (nativeHandoffTimeoutRef.current) {
      clearTimeout(nativeHandoffTimeoutRef.current);
      nativeHandoffTimeoutRef.current = null;
    }
    nativeHandoffPendingRef.current = false;
    setNativeHandoffPending(false);
  }, []);

  useEffect(() => {
    const resetWhenWebviewReturns = () => {
      if (document.visibilityState === "visible") {
        resetNativeHandoff();
      }
    };

    document.addEventListener("visibilitychange", resetWhenWebviewReturns);
    return () => {
      document.removeEventListener("visibilitychange", resetWhenWebviewReturns);
      if (nativeHandoffTimeoutRef.current) {
        clearTimeout(nativeHandoffTimeoutRef.current);
      }
    };
  }, [resetNativeHandoff]);

  const navigateToMyTrips = useCallback(() => {
    if (nativeHandoffPendingRef.current) {
      return;
    }

    if (!enableExternalTripsList) {
      navigateToLegacyMyTrips();
      return;
    }

    if (goToNativeMyTripsFromCheckout()) {
      nativeHandoffPendingRef.current = true;
      setNativeHandoffPending(true);
      nativeHandoffTimeoutRef.current = setTimeout(
        resetNativeHandoff,
        NATIVE_HANDOFF_TIMEOUT_MS
      );
      return;
    }

    navigateToLegacyMyTrips();
  }, [enableExternalTripsList, navigateToLegacyMyTrips, resetNativeHandoff]);

  return {
    navigateToMyTrips,
    nativeHandoffPending,
  };
};
